import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { Plus, MessageCircle, Clock, CheckCircle, Send, X, ArrowLeft, Sparkles, Zap, AlertCircle } from 'lucide-react';

const UserSupportTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showNewTicketForm, setShowNewTicketForm] = useState(false);
  const [newTicket, setNewTicket] = useState({
    subject: '',
    category: 'General',
    message: '',
    phone: ''
  });
  const { toast } = useToast();

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchTickets();
    const interval = setInterval(fetchTickets, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      if (!user) return;

      const userId = user.user_id || user.id;
      const response = await fetch(API_BASE + '/api/support/tickets/user/' + userId, {
        headers: { 'Authorization': 'Bearer ' + token }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTickets(data.tickets || []);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const createTicket = async () => {
    if (!newTicket.subject || !newTicket.message) {
      toast({ title: 'Error', description: 'Please fill all required fields', variant: 'destructive' });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;

      const response = await fetch(API_BASE + '/api/support/submit', {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: user.name,
          email: user.email,
          phone: newTicket.phone,
          subject: newTicket.subject,
          category: newTicket.category,
          message: newTicket.message,
          user_id: user.user_id || user.id,
          user_role: user.role
        })
      });

      if (response.ok) {
        toast({ title: 'Success', description: 'Support ticket created successfully' });
        setShowNewTicketForm(false);
        setNewTicket({ subject: '', category: 'General', message: '', phone: '' });
        fetchTickets();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to create ticket', variant: 'destructive' });
    }
  };

  const sendReply = async () => {
    if (!replyMessage.trim()) return;

    try {
      setSending(true);
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;

      const response = await fetch(API_BASE + '/api/support/tickets/reply', {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ticket_id: selectedTicket.id,
          reply_message: replyMessage,
          replied_by_name: user.name,
          replied_by_email: user.email,
          replied_by_role: user.role
        })
      });

      if (response.ok) {
        toast({ title: 'Success', description: 'Reply sent successfully' });
        setReplyMessage('');
        fetchTickets();
        const updatedTicket = tickets.find(t => t.id === selectedTicket.id);
        if (updatedTicket) setSelectedTicket(updatedTicket);
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to send reply', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const closeTicket = async (ticketId) => {
    try {
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;

      const response = await fetch(API_BASE + '/api/support/tickets/' + ticketId + '/close?closed_by=' + user.role, {
        method: 'PUT',
        headers: { 'Authorization': 'Bearer ' + token }
      });

      if (response.ok) {
        toast({ title: 'Success', description: 'Ticket closed successfully' });
        setSelectedTicket(null);
        fetchTickets();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to close ticket', variant: 'destructive' });
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      open: { label: 'Open', className: 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg' },
      in_progress: { label: 'In Progress', className: 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white shadow-lg animate-pulse' },
      closed: { label: 'Closed', className: 'bg-gradient-to-r from-gray-400 to-gray-500 text-white shadow-lg' }
    };
    return <Badge className={config[status].className}>{config[status].label}</Badge>;
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Technical': <Zap className="w-4 h-4" />,
      'Billing': <Sparkles className="w-4 h-4" />,
      'General': <MessageCircle className="w-4 h-4" />,
      'Account': <AlertCircle className="w-4 h-4" />
    };
    return icons[category] || <MessageCircle className="w-4 h-4" />;
  };

  const formatDate = (isoDate) => {
    return new Date(isoDate).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (showNewTicketForm) {
    return (
      <>
        <Button onClick={() => setShowNewTicketForm(false)} variant="ghost" className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Tickets
        </Button>
        <Card>
          <CardHeader className="bg-gradient-to-r from-red-600 via-black to-gray-800 text-white">
            <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <Sparkles className="w-5 h-5 sm:w-6 sm:h-6" />
              Create New Support Ticket
            </CardTitle>
          </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">Subject *</label>
                <Input
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket({...newTicket, subject: e.target.value})}
                  placeholder="Brief description of your issue"
                  className="border-2 border-red-200 dark:border-gray-600 focus:border-red-500 transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">Category *</label>
                <select
                  className="w-full p-3 border-2 border-red-200 dark:border-gray-600 rounded-md focus:border-red-500 dark:focus:border-red-400 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-900/30 transition-all bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  value={newTicket.category}
                  onChange={(e) => setNewTicket({...newTicket, category: e.target.value})}
                >
                  <option value="General" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">💬 General</option>
                  <option value="Technical" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">⚡ Technical Issue</option>
                  <option value="Billing" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">💳 Billing</option>
                  <option value="Account" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">👤 Account</option>
                  <option value="Bug Report" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">🐛 Bug Report</option>
                  <option value="Feature Request" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">✨ Feature Request</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">Phone (Optional)</label>
                <Input
                  value={newTicket.phone}
                  onChange={(e) => setNewTicket({...newTicket, phone: e.target.value})}
                  placeholder="Your phone number"
                  className="border-2 border-red-200 dark:border-gray-600 focus:border-red-500 transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">Message *</label>
                <Textarea
                  value={newTicket.message}
                  onChange={(e) => setNewTicket({...newTicket, message: e.target.value})}
                  placeholder="Describe your issue in detail..."
                  rows={8}
                  className="border-2 border-red-200 dark:border-gray-600 focus:border-red-500 transition-all"
                />
              </div>
              <Button 
                onClick={createTicket} 
                className="w-full bg-gradient-to-r from-red-600 to-black hover:from-red-700 hover:to-gray-900 text-white font-semibold py-6 shadow-lg transform hover:scale-105 transition-all"
              >
                <Send className="w-5 h-5 mr-2" />
                Submit Ticket
              </Button>
            </CardContent>
          </Card>
      </>
    );
  }

  if (selectedTicket) {
    return (
      <>
        <Button onClick={() => setSelectedTicket(null)} variant="ghost" className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Tickets
        </Button>
        <Card>
          <CardHeader className="bg-gradient-to-r from-red-600 via-black to-gray-800 text-white">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-2xl mb-2">{selectedTicket.subject}</CardTitle>
                  <p className="text-red-100 text-sm flex items-center gap-2">
                    {getCategoryIcon(selectedTicket.category)}
                    {selectedTicket.category} • Created {formatDate(selectedTicket.created_at)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusBadge(selectedTicket.status)}
                  {selectedTicket.status !== 'closed' && (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => closeTicket(selectedTicket.id)} 
                      className="border-2 border-white/30 hover:border-white/60 bg-white/10 hover:bg-white/20 text-white hover:text-white backdrop-blur-sm transition-all"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Close
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4 mb-6">
                <h3 className="font-bold text-lg text-gray-800 flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-red-600" />
                  Conversation
                </h3>
                <div className="space-y-3 max-h-96 overflow-y-auto pr-2 custom-scrollbar">
                  {selectedTicket.conversation?.map((msg, idx) => (
                    <div
                      key={idx}
                      className={'p-4 rounded-2xl shadow-md transform transition-all hover:scale-102 ' + 
                        (msg.by_role === 'admin' 
                          ? 'bg-gradient-to-br from-blue-100 to-cyan-100 ml-8 border-l-4 border-blue-500' 
                          : 'bg-gradient-to-br from-red-100 to-gray-100 mr-8 border-r-4 border-red-500')
                      }
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-sm text-gray-800">{msg.by_name}</span>
                        <span className="text-xs text-gray-500">{formatDate(msg.timestamp)}</span>
                      </div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{msg.message}</p>
                    </div>
                  ))}
                </div>
              </div>

              {selectedTicket.status !== 'closed' && (
                <div className="bg-gradient-to-br from-red-50 to-gray-50 p-6 rounded-2xl shadow-inner">
                  <h4 className="font-bold mb-3 text-gray-800 flex items-center gap-2">
                    <Send className="w-4 h-4 text-red-600" />
                    Add Reply
                  </h4>
                  <Textarea
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    placeholder="Type your message..."
                    rows={4}
                    className="mb-3 border-2 border-red-200 focus:border-red-500"
                  />
                  <Button
                    onClick={sendReply}
                    disabled={sending || !replyMessage.trim()}
                    className="w-full bg-gradient-to-r from-red-600 to-black hover:from-red-700 hover:to-gray-900 text-white font-semibold py-3 shadow-lg"
                  >
                    {sending ? 'Sending...' : 'Send Reply'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
      </>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl sm:text-2xl font-bold text-red-600">My Support Tickets</h3>
          <p className="text-sm text-gray-600 mt-1">View and manage your support requests</p>
        </div>
        <Button 
          onClick={() => setShowNewTicketForm(true)} 
          className="bg-gradient-to-r from-red-600 to-black hover:from-red-700 hover:to-gray-900 text-white text-sm sm:text-base"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Ticket
        </Button>
      </div>

      {loading ? (
        <Card>
          <CardContent className="py-12">
            <p className="text-center text-gray-500">Loading tickets...</p>
          </CardContent>
        </Card>
      ) : tickets.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="bg-gradient-to-br from-red-100 to-gray-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageCircle className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">No support tickets yet</h3>
            <p className="text-sm text-gray-600 mb-4">Create your first ticket to get help from our support team</p>
            <Button 
              onClick={() => setShowNewTicketForm(true)} 
              className="bg-gradient-to-r from-red-600 to-black hover:from-red-700 hover:to-gray-900 text-white"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Create Your First Ticket
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3 sm:space-y-4">
          {tickets.map(ticket => (
            <Card
              key={ticket.id}
              className="cursor-pointer hover:shadow-lg transition-all"
              onClick={() => setSelectedTicket(ticket)}
            >
              <CardContent className="p-4 sm:p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-bold text-xl text-gray-800">{ticket.subject}</h3>
                        {getStatusBadge(ticket.status)}
                        <Badge className="bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 flex items-center gap-1">
                          {getCategoryIcon(ticket.category)}
                          {ticket.category}
                        </Badge>
                      </div>
                      <p className="text-gray-600 dark:text-gray-400 line-clamp-2 mb-3">{ticket.message}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {formatDate(ticket.created_at)}
                        </span>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <MessageCircle className="w-4 h-4" />
                          {ticket.conversation?.length || 0} message(s)
                        </span>
                        {ticket.last_reply_by === 'admin' && ticket.status !== 'closed' && (
                          <Badge className="bg-gradient-to-r from-red-500 to-red-700 text-white animate-pulse shadow-lg">
                            ✨ New Reply
                          </Badge>
                        )}
                      </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <style jsx="true">{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #9333ea, #ec4899);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, #7e22ce, #db2777);
        }
      `}</style>
    </div>
  );
};

export default UserSupportTickets;
